import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  Grid,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  getEventStatusDisplay,
  formatDate,
  getWorkflowProgress,
} from "../utils/eventHelpers";
import { WORKFLOW_STEPS } from "../types/eventTypes";

const EventManager = ({
  event,
  onEdit,
  onDelete,
  onAssignStaff,
  onAssignEquipment,
  onAddSchedule,
  onAddExpense,
  onWorkflowUpdate,
  onRemoveStaff,
  onRemoveEquipment,
  onMarkEquipmentTaken,
  onMarkEquipmentReturned,
  onRemoveExpense,
}) => {
  const [tab, setTab] = useState(0);
  const statusDisplay = getEventStatusDisplay(event.status);
  const progress = getWorkflowProgress(event.workflow);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {event.eventDetails?.eventName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {event.id} • {event.eventDetails?.eventType} •{" "}
              {event.eventDetails?.customer?.customerName}
            </Typography>
          </Box>
          <Chip label={statusDisplay.label} color={statusDisplay.color} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </Box>
      </Paper>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        <Tab label="Staff" />
        <Tab label="Equipment" />
        <Tab label="Schedule" />
        <Tab label="Expenses" />
        <Tab label="Workflow" />
        <Tab label="Finance" />
      </Tabs>

      {/* Staff Tab */}
      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Assigned Staff</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={onAssignStaff}
              size="small"
            >
              Add Staff
            </Button>
          </Box>
          <List>
            {(event.staff || []).map((s) => (
              <ListItem
                key={s.userId}
                divider
                secondaryAction={
                  <IconButton
                    color="error"
                    onClick={() => onRemoveStaff(s.userId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${s.name} — ${s.role}`}
                  secondary={`${s.phone} | ₹${s.salary} | ${s.reportingTime} - ${s.releaseTime}`}
                  secondaryTypographyProps={{ component: "div" }}
                />
                <Chip label={s.status} size="small" sx={{ mr: 2 }} />
              </ListItem>
            ))}
            {(!event.staff || event.staff.length === 0) && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No staff assigned yet
              </Typography>
            )}
          </List>
        </Paper>
      )}

      {/* Equipment Tab */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Required Equipment</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={onAssignEquipment}
              size="small"
            >
              Add Equipment
            </Button>
          </Box>
          <List>
            {(event.requiredEquipments || []).map((eq) => (
              <ListItem
                key={eq.equipmentId}
                divider
                secondaryAction={
                  <IconButton
                    color="error"
                    onClick={() => onRemoveEquipment(eq.equipmentId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${eq.equipmentName} (x${eq.quantity})`}
                  secondary={eq.category}
                  secondaryTypographyProps={{ component: "div" }}
                />
                <Box sx={{ display: "flex", gap: 1, mr: 2 }}>
                  <Chip
                    label={eq.officeTaken ? "Taken" : "Not Taken"}
                    color={eq.officeTaken ? "warning" : "default"}
                    size="small"
                    onClick={() =>
                      !eq.officeTaken && onMarkEquipmentTaken(eq.equipmentId)
                    }
                  />
                  <Chip
                    label={eq.returned ? "Returned" : "Not Returned"}
                    color={eq.returned ? "success" : "default"}
                    size="small"
                    onClick={() =>
                      eq.officeTaken &&
                      !eq.returned &&
                      onMarkEquipmentReturned(eq.equipmentId)
                    }
                  />
                </Box>
              </ListItem>
            ))}
            {(!event.requiredEquipments ||
              event.requiredEquipments.length === 0) && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No equipment added yet
              </Typography>
            )}
          </List>
        </Paper>
      )}

      {/* Schedule Tab */}
      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Shoot Schedule</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={onAddSchedule}
              size="small"
            >
              Add Day
            </Button>
          </Box>
          <Grid container spacing={2}>
            {(event.schedules || []).map((s) => (
              <Grid item xs={12} sm={6} key={s.id}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography fontWeight="bold">{s.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(s.date)} • {s.location}
                  </Typography>
                  <Typography variant="body2">
                    {s.startTime} - {s.endTime}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Staff: {s.staffIds?.length || 0} assigned
                  </Typography>
                </Paper>
              </Grid>
            ))}
            {(!event.schedules || event.schedules.length === 0) && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No shoot days scheduled yet
              </Typography>
            )}
          </Grid>
        </Paper>
      )}

      {/* Expenses Tab */}
      {tab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Expenses</Typography>
            <Button startIcon={<AddIcon />} onClick={onAddExpense} size="small">
              Add Expense
            </Button>
          </Box>
          <List>
            {(event.expenses || []).map((ex) => (
              <ListItem
                key={ex.expenseId}
                divider
                secondaryAction={
                  <IconButton
                    color="error"
                    onClick={() => onRemoveExpense(ex.expenseId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${ex.category} — ₹${ex.amount}`}
                  secondary={`${ex.paymentMethod} | Paid by ${ex.paidBy} | ${ex.note}`}
                />
              </ListItem>
            ))}
            {(!event.expenses || event.expenses.length === 0) && (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No expenses recorded yet
              </Typography>
            )}
          </List>
        </Paper>
      )}

      {/* Workflow Tab */}
      {tab === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Post-Production Workflow
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 2, height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary">
            {progress}% completed
          </Typography>
          <Grid container sx={{ mt: 1 }}>
            {WORKFLOW_STEPS.map((step) => (
              <Grid item xs={12} sm={6} key={step}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!event.workflow?.[step]}
                      onChange={(e) =>
                        onWorkflowUpdate({ [step]: e.target.checked })
                      }
                    />
                  }
                  label={step
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (c) => c.toUpperCase())}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Finance Tab */}
      {tab === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Quotation & Invoice
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography color="text.secondary">Quotation Amount</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>₹{event.quotation?.amount || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Invoice Total</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>₹{event.invoice?.total || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Advance Paid</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>₹{event.invoice?.advance || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Balance Due</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                color={event.invoice?.balance > 0 ? "error" : "success.main"}
              >
                ₹{event.invoice?.balance || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Payment Status</Typography>
            </Grid>
            <Grid item xs={6}>
              <Chip label={event.invoice?.paymentStatus} size="small" />
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default EventManager;
